import {CoreDI, DeviceType, lazyInject, ResolutionManager, ScreenParameters, ScreenOrientation, ScreenManager} from '@gongxigames/framework/core';
import {ISystemPopupView} from './index';
import {HTMLSystemPopup} from '@gongxigames/framework/slot/types';
import {PopupObject} from '@gongxigames/framework/slot/services/TemplateUtils';

const HTML_POPUP_TYPE = 'HTML';
const HTML_POPUP_MARGIN = 20;

export class HTMLSystemPopupsView implements ISystemPopupView {

    @lazyInject(CoreDI.ResolutionManager)
    protected _resolutionManager!: ResolutionManager;

    @lazyInject(CoreDI.ScreenManager)
    protected _screenManager!: ScreenManager;

    protected currentPopup?: HTMLSystemPopup;
    protected resizeData?: ScreenParameters;

    protected canvas!: Element;

    constructor(
        private readonly platform: DeviceType
    ) {}

    public init(screenParameters: ScreenParameters): void {
        this.canvas = document.querySelector('body > canvas')!;
        this.resizeData = screenParameters;
    }

    public destroy(): void {
        this.hideCurrentPopup();
    }

    public show(popup: HTMLSystemPopup): void {
        if (popup.type !== HTML_POPUP_TYPE) {
            return;
        }

        this.currentPopup = popup;

        this.addPopupToParent(popup);
        this.resizeCurrentPopup();

        popup.view.parent.hidden = false;
    }

    public hide(popup: HTMLSystemPopup): void {
        if (popup.type !== HTML_POPUP_TYPE) {
            return;
        }

        if (this.currentPopup && popup.id === this.currentPopup.id) {
            this.hideCurrentPopup();
        } else {
            this.removePopupFromParent(popup);
        }
    }

    public hideCurrentPopup(): void {
        if (this.currentPopup) {
            this.removePopupFromParent(this.currentPopup);
            this.currentPopup = undefined;
        }
    }

    public resize(resizeData: ScreenParameters): void {
        this.resizeData = resizeData;
        this.resizeCurrentPopup();
    }

    protected addPopupToParent({view}: HTMLSystemPopup): void {
        if (!view.parent.parentElement) {
            document.body.insertAdjacentElement('beforeend', view.parent);
        }
    }

    protected removePopupFromParent({view}: HTMLSystemPopup): void {
        if (view.parent.parentElement) {
            view.parent.hidden = true;
            document.body.removeChild(view.parent);
        }
    }

    protected resizeCurrentPopup(): void {
        if (!this.currentPopup) {
            return;
        }

        const popup = (this.currentPopup.view as PopupObject).popup;

        const resize = () => {
            popup.style.transform = '';

            let systemScale = this.getScale();
            const viewportHeight = window.innerHeight - HTML_POPUP_MARGIN * 2;
            const naturalHeight = popup.offsetHeight;

            if (naturalHeight * systemScale > viewportHeight) {
                systemScale = viewportHeight / naturalHeight;
            }

            popup.style.transform = `scale(${systemScale}) translate(-50%, -50%)`;
        };

        resize();
        // popup.offsetHeight is updated to the right value only after the next frame
        // (or at least not on this callstack) so we need calculate the scale after the next frame
        setTimeout(resize, 1);
    }

    protected getScale(): number {
        const resolution = this._screenManager.defaultViewportResolution;
        const resolutionScale = this.getClientWidth() / this.getResolutionWidth();

        return resolution * resolutionScale;
    }

    protected getResolutionWidth(): number {
        const resolution = this._resolutionManager?.resolution || {width: 1, height: 1};
        const orientation = this.resizeData?.orientation;
        const isDesktop = this.platform === DeviceType.DESKTOP;
        const isLandscape = orientation === ScreenOrientation.LANDSCAPE;

        return (isDesktop || isLandscape) ? resolution.width : resolution.height;
    }

    protected getClientWidth(): number {
        return this.canvas.clientWidth;
    }
}
